import dynamic from "next/dynamic";
import Image from "next/image";
import React from "react";

import BlogImage from "@/components/blog/BlogImage";
import ResourcesCTA from "@/components/ResourcesCTA";

const ReactPlayer = dynamic(() => import("react-player"), { ssr: false });

function YouTubeEmbed({ url }: { url: string }) {
  return (
    <div className="relative my-8 mx-auto pt-[56.25%] not-prose" itemProp="video" itemScope itemType="https://schema.org/VideoObject">
      <ReactPlayer
        src={url}
        playing={false}
        controls
        className="absolute top-0 left-0 w-full h-full"
        width="100%"
        height="100%"
      />
    </div>
  );
}

function QuoteBlock({ children, cite }: { children: React.ReactNode; cite?: string }) {
  return (
    <blockquote className="border-l-4 border-rebolt-500 pl-4 italic text-ash-700 my-6">
      {children}
      {cite && <footer className="mt-2 text-sm text-ash-500 not-italic">— {cite}</footer>}
    </blockquote>
  );
}

function MdxImage(props: React.ImgHTMLAttributes<HTMLImageElement>) {
  const { src, alt } = props;
  if (!src) return null;

  if (typeof src === "string" && (src.startsWith("/") || src.startsWith("http"))) {
    return (
      <figure className="my-8 not-prose">
        <Image src={src} alt={alt ?? ""} width={800} height={500} quality={70} className="mx-auto rounded-lg" />
        {alt && <figcaption className="mt-1 text-center text-sm text-ash-500">{alt}</figcaption>}
      </figure>
    );
  }

  // eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
  return <img {...props} />;
}

function MdxLink(props: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const { href, children, ...rest } = props;
  const isExternal = href?.startsWith("http");

  return (
    <a href={href} {...(isExternal ? { target: "_blank", rel: "noreferrer noopener" } : {})} {...rest}>
      {children}
    </a>
  );
}

export const mdxComponents = {
  img: MdxImage,
  a: MdxLink,
  BlogImage,
  YouTubeEmbed,
  QuoteBlock,
  ResourcesCTA,
};
